import { useState, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { photoUploadService } from "@/services/photoUploadService";
import { validateImageContentEnhanced } from "@/utils/enhancedContentModeration";

interface UsePhotoUploadProps {
  photos: string[];
  onPhotosChange: (photos: string[]) => void;
  maxPhotos?: number;
}

export const usePhotoUpload = ({ photos, onPhotosChange, maxPhotos = 5 }: UsePhotoUploadProps) => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState<boolean[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || !user) return;
    
    const remainingSlots = maxPhotos - photos.length;
    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    
    if (filesToProcess.length === 0) {
      toast({
        title: "Maximum photos reached",
        description: `You can only upload up to ${maxPhotos} photos.`,
        variant: "destructive",
      });
      return;
    }

    const newUploadingStates = new Array(filesToProcess.length).fill(true);
    setUploading(prev => [...prev, ...newUploadingStates]);

    try {
      const uploadPromises = filesToProcess.map(async (file, index) => {
        try {
          console.log(`🔍 Starting enhanced validation for file ${index + 1}:`, file.name, 'Type:', file.type);
          
          // Enhanced content moderation with Apple Store compliance
          const contentCheck = await validateImageContentEnhanced(file, user.id);
          
          if (!contentCheck.isAppropriate) {
            console.log(`❌ Enhanced validation failed for ${file.name}:`, contentCheck.reason);
            
            // Check if this is a HEIC-specific issue
            const isHEICFile = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
            if (isHEICFile && contentCheck.reason?.includes('browser')) {
              throw new Error('HEIC files from iPhone may not be fully supported in this browser. Please try converting the image to JPEG format first, or try using a different browser like Safari.');
            }
            
            throw new Error(contentCheck.reason || "This image violates our content policy and Apple Store guidelines.");
          }

          if (!contentCheck.applestoreCompliant) {
            console.log(`⚠️ Apple Store compliance issue for ${file.name}`);
            throw new Error("This image may not meet Apple Store content guidelines. Please choose a different photo.");
          }

          console.log(`✅ Enhanced validation passed for ${file.name}, proceeding with upload`);
          return photoUploadService.uploadPhoto(file, user.id);
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
          throw error;
        }
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      onPhotosChange([...photos, ...uploadedUrls]);
      
      // Show success modal instead of toast
      setUploadCount(uploadedUrls.length);
      setShowSuccessModal(true);
    } catch (error: any) {
      console.error('Enhanced upload process failed:', error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload image due to content policy violations.",
        variant: "destructive",
      });
    } finally {
      setUploading([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemovePhoto = async (photoUrl: string, index: number) => {
    if (!user) return;

    try {
      await photoUploadService.deletePhoto(photoUrl, user.id);

      const newPhotos = photos.filter((_, i) => i !== index);
      onPhotosChange(newPhotos);
      
      toast({
        title: "Photo removed",
        description: "Photo has been deleted from your profile.",
      });
    } catch (error: any) {
      toast({
        title: "Error removing photo",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleDragDrop = (files: FileList) => {
    handleFileSelect(files);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const uploadSinglePhoto = async (file: File): Promise<string> => {
    if (!user) throw new Error("User not authenticated");
    
    console.log('🔍 Starting single photo upload with enhanced validation for:', file.name);
    
    // Enhanced content moderation with Apple Store compliance
    const contentCheck = await validateImageContentEnhanced(file, user.id);
    
    if (!contentCheck.isAppropriate) {
      console.log('❌ Single photo enhanced validation failed:', contentCheck.reason);
      throw new Error(contentCheck.reason || "This image violates our content policy and Apple Store guidelines.");
    }

    if (!contentCheck.applestoreCompliant) {
      console.log('⚠️ Single photo Apple Store compliance issue');
      throw new Error("This image may not meet Apple Store content guidelines. Please choose a different photo.");
    }
    
    console.log('✅ Single photo enhanced validation passed, proceeding with upload');
    return photoUploadService.uploadPhoto(file, user.id);
  };

  return {
    uploading,
    fileInputRef,
    handleFileSelect,
    handleRemovePhoto,
    handleDragDrop,
    openFileDialog,
    uploadSinglePhoto,
    showSuccessModal,
    setShowSuccessModal,
    uploadCount
  };
};
